import { ScriptOnce } from "@tanstack/react-router";
import { createContext, use, useCallback, useEffect, useMemo, useState } from "react";

type Theme = "dark" | "light" | "system";
type ResolvedTheme = Exclude<Theme, "system">;
const MEDIA = "(prefers-color-scheme: dark)";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
  forcedTheme?: ResolvedTheme;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  forcedTheme?: ResolvedTheme;
};

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
  forcedTheme: undefined,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

// references:
// https://ui.shadcn.com/docs/dark-mode/vite
// https://github.com/pacocoursey/next-themes/blob/main/next-themes/src/index.tsx
export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "theme",
  forcedTheme,
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () =>
      forcedTheme ||
      (typeof window !== "undefined"
        ? (localStorage.getItem(storageKey) as Theme)
        : null) || defaultTheme,
  );

  const handleMediaQuery = useCallback(
    (e: MediaQueryListEvent | MediaQueryList) => {
      if (forcedTheme) return;
      if (theme !== "system") return;
      const root = window.document.documentElement;
      const targetTheme = e.matches ? "dark" : "light";
      if (!root.classList.contains(targetTheme)) {
        root.classList.remove("light", "dark");
        root.classList.add(targetTheme);
      }
    },
    [forcedTheme, theme],
  );

  // Listen for system preference changes
  useEffect(() => {
    if (forcedTheme) return;
    const media = window.matchMedia(MEDIA);

    media.addEventListener("change", handleMediaQuery);
    handleMediaQuery(media);

    return () => media.removeEventListener("change", handleMediaQuery);
  }, [forcedTheme, handleMediaQuery]);

  useEffect(() => {
    const root = window.document.documentElement;

    let targetTheme: ResolvedTheme;

    if (forcedTheme) {
      targetTheme = forcedTheme;
    } else if (theme === "system") {
      localStorage.removeItem(storageKey);
      targetTheme = window.matchMedia(MEDIA).matches ? "dark" : "light";
    } else {
      localStorage.setItem(storageKey, theme);
      targetTheme = theme;
    }

    // Only update if the target theme is not already applied
    if (!root.classList.contains(targetTheme)) {
      root.classList.remove("light", "dark");
      root.classList.add(targetTheme);
    }
  }, [forcedTheme, theme, storageKey]);

  const value = useMemo(
    () => ({
      theme: forcedTheme ?? theme,
      setTheme: forcedTheme ? () => null : setTheme,
      forcedTheme,
    }),
    [forcedTheme, theme],
  );

  const initialThemeScript = forcedTheme
    ? `document.documentElement.classList.remove('dark');document.documentElement.classList.add('light');`
    : `const savedTheme=localStorage.getItem('${storageKey}');const prefersDark=window.matchMedia('${MEDIA}').matches;const theme=savedTheme??(prefersDark?'dark':'light');document.documentElement.classList.remove('light','dark');document.documentElement.classList.add(theme==='dark'?'dark':'light');`;

  return (
    <ThemeProviderContext value={value}>
      <ScriptOnce>
        {/* Apply theme early to avoid FOUC */}
        {initialThemeScript}
      </ScriptOnce>
      {children}
    </ThemeProviderContext>
  );
}

export const useTheme = () => {
  const context = use(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
